import { graphql } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'

import { MentionHeader, MentionHeader_Mention } from './MentionHeader'
import {
  EngagementAvatar,
  EngagementAvatar_Channel,
} from '../../components/EngagementAvatar'
import { EngagementItem } from '../../components/EngagementItem'
import { MentionContextProvider } from '../context/MentionContext'
import type { MentionData } from '../context/MentionContext'
import { MentionMedia } from '../MentionMedia'

import { MentionedChannelText_Channel } from '../../components/MentionedChannelText'
import { MentionText } from './MentionText'

import type { JSX } from 'react'

export const MentionLastReplies_Channel = graphql(
  /* GraphQL */ `
    fragment MentionLastReplies_Channel on Channel @_unmask {
      avatar
      ...EngagementAvatar_Channel
      ...MentionedChannelText_Channel
    }
  `,
  [EngagementAvatar_Channel, MentionedChannelText_Channel],
)

export const MentionLastReplies_Mention = graphql(
  /* GraphQL */ `
    fragment MentionLastReplies_Mention on Mention @_unmask {
      id
      isOwnEngagement
      text
      author {
        username
        avatar
      }
      ...MentionHeader_Mention
    }
  `,
  [MentionHeader_Mention],
)

type MentionLastReplyProps = {
  channel: FragmentOf<typeof MentionLastReplies_Channel>
  reply: MentionData
  showConnector?: boolean
  showChannelBadge?: boolean
}

/**
 * A single reply rendered below the main mention: a plain conversation row
 * (avatar, header, text, media) with none of the inbox-card chrome. Your own
 * replies show the channel avatar, since they were sent as the channel;
 * everyone else's show the author's avatar.
 */
const MentionLastReply = ({
  channel,
  reply,
  showConnector = false,
  showChannelBadge = false,
}: MentionLastReplyProps): JSX.Element => {
  return (
    <MentionContextProvider mention={reply}>
      <EngagementItem
        as="article"
        variant="lastReply"
        aria-label={reply.isOwnEngagement ? 'Your reply' : "Author's reply"}
        aria-labelledby={`mention-header-${reply.id}`}
        data-testid="mention-item"
        data-reply-id={reply.id}
        avatar={
          <EngagementAvatar
            hasParent
            showConnector={showConnector}
            avatar={
              reply.isOwnEngagement
                ? channel.avatar
                : (reply.author?.avatar ?? '')
            }
            username={reply.author?.username ?? ''}
            channel={showChannelBadge ? channel : undefined}
          />
        }
      >
        <MentionHeader isReply />
        {reply.text ? (
          <MentionText
            text={reply.text}
            // Your own replies don't highlight your own name.
            channel={reply.isOwnEngagement ? undefined : channel}
          />
        ) : null}
        <MentionMedia />
      </EngagementItem>
    </MentionContextProvider>
  )
}

MentionLastReply.displayName = 'MentionLastReply'

type MentionLastRepliesProps = {
  channel: FragmentOf<typeof MentionLastReplies_Channel>
  replies: MentionData[]
  /** Whether a reply form renders below; keeps the last connector threaded. */
  isFormVisible: boolean
  /** Whether a "Sending…" row renders below; also keeps the connector threaded. */
  isProcessing?: boolean
  /** Badges each reply avatar with the channel's service icon, as the main mention does. */
  showChannelBadge?: boolean
}

/**
 * Renders a replied mention's replies below the main mention. Replies arrive
 * oldest-first, so the newest sits closest to the reply form at the bottom.
 */
export const MentionLastReplies = ({
  channel,
  replies,
  isFormVisible,
  isProcessing = false,
  showChannelBadge = false,
}: MentionLastRepliesProps): JSX.Element | null => {
  if (replies.length === 0) {
    return null
  }

  return (
    <>
      {replies.map((reply, index) => {
        const isLast = index === replies.length - 1
        // Thread the connector to whatever sits below: another reply, the
        // "Sending…" row, or the reply form.
        const showConnector = !isLast || isProcessing || isFormVisible

        return (
          <MentionLastReply
            key={reply.id}
            channel={channel}
            reply={reply}
            showConnector={showConnector}
            showChannelBadge={showChannelBadge}
          />
        )
      })}
    </>
  )
}

MentionLastReplies.displayName = 'MentionLastReplies'
